import { useMemo, useState, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { ZodType } from "zod";
import { apiFetch } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { UseFormReturn, FieldValues, DefaultValues } from "react-hook-form";

interface Paged<T> { items: T[]; page: number; pageSize: number; totalCount: number; }

export function LookupListPage<TRow extends { deleted: boolean }, TForm extends FieldValues>({
  title, apiPath, requiredPermission, columns, rowKey, schema,
  toFormValues, toApiBody, blankFormValues, fields,
}: {
  title: string;
  apiPath: string;
  requiredPermission?: string;
  columns: readonly DataGridColumn<TRow>[];
  rowKey: (r: TRow) => string | number;
  schema: ZodType<TForm, TForm>;
  toFormValues: (row: TRow) => DefaultValues<TForm>;
  toApiBody: (form: TForm) => unknown;
  blankFormValues: DefaultValues<TForm>;
  fields: (form: UseFormReturn<TForm>) => ReactNode;
}) {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [editing, setEditing] = useState<TRow | null>(null);
  const [creating, setCreating] = useState(false);
  void requiredPermission;

  const list = useQuery({
    queryKey: [apiPath, page, includeDeleted],
    queryFn: () => apiFetch<Paged<TRow>>(`${apiPath}?page=${page}&pageSize=50${includeDeleted ? "" : "&deleted=false"}`),
  });

  const upsert = useMutation({
    mutationFn: (form: TForm) => editing
      ? apiFetch<TRow>(`${apiPath}/${rowKey(editing)}`, { method: "PATCH", body: JSON.stringify(toApiBody(form)) })
      : apiFetch<TRow>(apiPath,                            { method: "POST",  body: JSON.stringify(toApiBody(form)) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [apiPath] }); setEditing(null); setCreating(false); },
  });

  const enrichedColumns: DataGridColumn<TRow>[] = useMemo(
    () => [
      ...columns,
      { id: "deleted", header: "Status", sortable: true, accessor: (r: TRow) => r.deleted ? "(deleted)" : "Active" },
    ],
    [columns],
  );

  // Sort state — client-side only; lookup endpoints have no `sort` param.
  // Sort applies to the current page's rows, not the full result set.
  // Respects per-column `sortable` flags passed in by callers, plus the
  // wrapper-appended "Status" column. The comparator only handles accessors
  // that return primitives (string/number/boolean); accessors returning React
  // elements (e.g. an underlined edit button) fall back to an empty string and
  // therefore sort as ties. Callers that need richer ordering on rich-cell
  // columns shouldn't mark those columns sortable.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const sortedRows = useMemo(() => {
    const rows = list.data?.items ?? [];
    if (!sort) return rows;
    const col = enrichedColumns.find((c) => c.id === sort.columnId);
    if (!col) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const toComparable = (r: TRow): string | number | null => {
      const v = col.accessor(r);
      if (v == null || typeof v === "boolean") return null;
      if (typeof v === "string" || typeof v === "number") return v;
      // ReactNode fallback — treat as untyped (sort as ties).
      return null;
    };
    return [...rows].sort((a, b) => {
      const va = toComparable(a);
      const vb = toComparable(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [list.data, sort, enrichedColumns]);

  return (
    <ListPage backTo="/admin"
      title={title}
      primaryAction={<IntentButton onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add {title.replace(/s$/, "")}</IntentButton>}
      toolbarExtra={<div className="flex items-center gap-2"><Switch id="d" checked={includeDeleted} onCheckedChange={setIncludeDeleted} /><Label htmlFor="d">Show deleted</Label></div>}>
      <DataGrid rows={sortedRows} columns={enrichedColumns} rowKey={rowKey}
        totalRows={list.data?.totalCount ?? 0} pageIndex={page} pageSize={50}
        loading={list.isLoading} onPageChange={setPage} onSortChange={setSort} />
      <DetailModal
        open={creating || editing !== null}
        onOpenChange={(o) => { if (!o) { setEditing(null); setCreating(false); } }}
        title={editing ? `Edit ${title.replace(/s$/, "")}` : `New ${title.replace(/s$/, "")}`}>
        <EntityForm
          schema={schema}
          defaultValues={editing ? toFormValues(editing) : blankFormValues}
          onSubmit={(v) => upsert.mutate(v)}>
          {(form) => fields(form)}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
